import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  SideNavigation,
  SideNavigationItem,
  SideNavigationSubItem
} from '@ui5/webcomponents-react';
import usePermission from '../hooks/usePermission';
import useAutoTranslate from '../hooks/useAutoTranslate';

function SideMenu({ isOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, isAdmin, isSaasAdmin } = usePermission();
  const { autoT } = useAutoTranslate();

  const [expandedItems, setExpandedItems] = useState({
    ordenesCompra: false,
    ordenesVenta: false,
    notificaciones: false,
    ramplaVirtual: false,
    socios: false,
    configuracion: false
  });

  const [expandedConfigGroups, setExpandedConfigGroups] = useState({
    almacen: false,
    rampla: false,
    usuarios: false
  });

  const toggleExpand = (section) => {
    setExpandedItems(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleConfigGroup = (group) => {
    setExpandedConfigGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const isCurrentPath = (path) => location.pathname === path;

  const requestLogout = () => {
    window.dispatchEvent(new CustomEvent('faros:request-logout'));
  };

  return (
    <div style={{
      width: isOpen ? '250px' : '0',
      transition: 'width 0.3s ease-in-out',
      overflow: 'hidden',
      backgroundColor: '#223E5D',
      height: '100%',
      position: 'fixed',
      zIndex: 1000,
      '@media (max-width: 768px)': {
        width: isOpen ? '100%' : '0',
        position: 'fixed',
        top: '48px',
        left: 0
      },
      visibility: isOpen ? 'visible' : 'hidden'
    }}>
      <SideNavigation style={{
        opacity: isOpen ? 1 : 0,
        transition: 'opacity 0.3s ease-in-out'
      }}>

        {/* ÓRDENES DE COMPRA */}
        {hasPermission('buyorder', 'listView') && (
          <SideNavigationItem
            icon="cart"
            text={autoT("Órdenes de Compra")}
            expanded={expandedItems.ordenesCompra}
            onClick={() => toggleExpand('ordenesCompra')}
            selected={isCurrentPath('/orderlist') || isCurrentPath('/schedule-list')}
          >
            <SideNavigationSubItem
              icon="history"
              text={autoT("Historial de OC")}
              onClick={() => navigate('/orderlist')}
              selected={isCurrentPath('/orderlist')}
            />
            {hasPermission('schedules', 'listView') && (
              <SideNavigationSubItem
                icon="calendar"
                text={autoT("Agendamiento de OC")}
                onClick={() => navigate('/schedule-list')}
                selected={isCurrentPath('/schedule-list')}
              />
            )}
          </SideNavigationItem>
        )}

        {/* ÓRDENES DE VENTA */}
        {(hasPermission('salesorder', 'listView') || hasPermission('branding', 'listView') || hasPermission('saleschannel', 'listView')) && (
          <SideNavigationItem
            icon="sales-order"
            text={autoT("Órdenes de Venta")}
            expanded={expandedItems.ordenesVenta}
            onClick={() => toggleExpand('ordenesVenta')}
            selected={isCurrentPath('/sales-orders') || isCurrentPath('/brandings') || isCurrentPath('/sale-channels')}
          >
            {hasPermission('salesorder', 'listView') && (
              <SideNavigationSubItem
                icon="history"
                text={autoT("Historial de OV")}
                onClick={() => navigate('/sales-orders')}
                selected={isCurrentPath('/sales-orders')}
              />
            )}
            {hasPermission('branding', 'listView') && (
              <SideNavigationSubItem
                icon="tag"
                text={autoT("Marcas")}
                onClick={() => navigate('/brandings')}
                selected={isCurrentPath('/brandings')}
              />
            )}
            {hasPermission('saleschannel', 'listView') && (
              <SideNavigationSubItem
                icon="sales-order"
                text={autoT("Canales de Venta")}
                onClick={() => navigate('/sale-channels')}
                selected={isCurrentPath('/sale-channels')}
              />
            )}
          </SideNavigationItem>
        )}

        {/* NOTIFICACIONES DE ENTREGA */}
        {hasPermission('notification', 'listView') && (
          <SideNavigationItem
            icon="notification-2"
            text={autoT("Notificaciones de Entrega")}
            expanded={expandedItems.notificaciones}
            onClick={() => toggleExpand('notificaciones')}
            selected={location.pathname.includes('/notification') || isCurrentPath('/sending')}
          >
            <SideNavigationSubItem
              icon="history"
              text={autoT("Historial de Notificaciones")}
              onClick={() => navigate('/notificationslist', { state: { activePart: 'ASNList' } })}
              selected={isCurrentPath('/notificationslist')}
            />
            {hasPermission('grn', 'listView') && (
              <SideNavigationSubItem
                icon="shipping-status"
                text={autoT("Historial de Entregas")}
                onClick={() => navigate('/sending')}
                selected={isCurrentPath('/sending')}
              />
            )}
          </SideNavigationItem>
        )}

        {/* RAMPLA VIRTUAL */}
        {hasPermission('virtualramp', 'listView') && (
          <SideNavigationItem
            icon="shipping-status"
            text={autoT("Rampla Virtual")}
            expanded={expandedItems.ramplaVirtual}
            onClick={() => toggleExpand('ramplaVirtual')}
            selected={isCurrentPath('/ramplas') || isCurrentPath('/create-ramp')}
          >
            {hasPermission('virtualramp', 'create') && (
              <SideNavigationSubItem
                icon="add"
                text={autoT("Crear Rampla Virtual")}
                onClick={() => navigate('/create-ramp')}
                selected={isCurrentPath('/create-ramp')}
              />
            )}
            <SideNavigationSubItem
              icon="history"
              text={autoT("Historial de Ramplas")}
              onClick={() => navigate('/ramplas')}
              selected={isCurrentPath('/ramplas')}
            />
          </SideNavigationItem>
        )}

        {/* SOCIOS COMERCIALES */}
        {(isAdmin() || hasPermission('companies', 'listView')) && (
          <SideNavigationItem
            icon="group"
            text={autoT("Socios Comerciales")}
            expanded={expandedItems.socios}
            onClick={() => toggleExpand('socios')}
            selected={isCurrentPath('/companies') || isCurrentPath('/suppliers')}
          >
            <SideNavigationSubItem
              icon="business-card"
              text={autoT("Mandante / Cliente")}
              onClick={() => navigate('/companies')}
              selected={isCurrentPath('/companies')}
            />
            <SideNavigationSubItem
              icon="factory"
              text={autoT("Proveedor")}
              onClick={() => navigate('/suppliers')}
              selected={isCurrentPath('/suppliers')}
            />
          </SideNavigationItem>
        )}

        {/* CONFIGURACIÓN */}
        {(hasPermission('settings', 'listView') ||
          hasPermission('district', 'listView') ||
          hasPermission('centers', 'listView') ||
          hasPermission('warehouses', 'listView') ||
          hasPermission('shippingmethod', 'listView') ||
          hasPermission('trucks', 'listView') ||
          hasPermission('truckstypes', 'listView') ||
          hasPermission('users', 'listView') ||
          hasPermission('roles', 'listView')) && (
          <SideNavigationItem
            icon="settings"
            text={autoT("Configuración")}
            expanded={expandedItems.configuracion}
            onClick={() => toggleExpand('configuracion')}
            selected={isCurrentPath('/system-config') ||
              isCurrentPath('/districts') || isCurrentPath('/centers') || isCurrentPath('/warehouses') || isCurrentPath('/shipping-methods') ||
              isCurrentPath('/truck-types') || isCurrentPath('/trucks') ||
              isCurrentPath('/roles') || isCurrentPath('/user-list')}
          >
            {hasPermission('settings', 'listView') && (
              <SideNavigationSubItem
                icon="action-settings"
                text={autoT("Config Sistema")}
                onClick={() => navigate('/system-config')}
                selected={isCurrentPath('/system-config')}
              />
            )}

            {/* Grupo Almacén */}
            {(hasPermission('district', 'listView') || hasPermission('centers', 'listView') || hasPermission('warehouses', 'listView') || hasPermission('shippingmethod', 'listView')) && (
              <SideNavigationSubItem
                icon="product"
                text={expandedConfigGroups.almacen ? "▼ Almacén" : "▶ Almacén"}
                onClick={() => toggleConfigGroup('almacen')}
                selected={isCurrentPath('/districts') || isCurrentPath('/centers') || isCurrentPath('/warehouses') || isCurrentPath('/shipping-methods')}
              />
            )}
            {expandedConfigGroups.almacen && hasPermission('district', 'listView') && (
              <SideNavigationSubItem
                icon="map"
                text={autoT("    Distritos")}
                onClick={() => navigate('/districts')}
                selected={isCurrentPath('/districts')}
              />
            )}
            {expandedConfigGroups.almacen && hasPermission('centers', 'listView') && (
              <SideNavigationSubItem
                icon="official-service"
                text={autoT("    Centros")}
                onClick={() => navigate('/centers')}
                selected={isCurrentPath('/centers')}
              />
            )}
            {expandedConfigGroups.almacen && hasPermission('warehouses', 'listView') && (
              <SideNavigationSubItem
                icon="shipping-status"
                text={autoT("    Bodegas")}
                onClick={() => navigate('/warehouses')}
                selected={isCurrentPath('/warehouses')}
              />
            )}
            {expandedConfigGroups.almacen && hasPermission('shippingmethod', 'listView') && (
              <SideNavigationSubItem
                icon="shipping-status"
                text={autoT("    Método de Envío")}
                onClick={() => navigate('/shipping-methods')}
                selected={isCurrentPath('/shipping-methods')}
              />
            )}

            {/* Grupo Rampla Virtual */}
            {(hasPermission('truckstypes', 'listView') || hasPermission('trucks', 'listView')) && (
              <SideNavigationSubItem
                icon="bus-public-transport"
                text={expandedConfigGroups.rampla ? "▼ Rampla Virtual" : "▶ Rampla Virtual"}
                onClick={() => toggleConfigGroup('rampla')}
                selected={isCurrentPath('/truck-types') || isCurrentPath('/trucks')}
              />
            )}
            {expandedConfigGroups.rampla && hasPermission('truckstypes', 'listView') && (
              <SideNavigationSubItem
                icon="multiselect-none"
                text={autoT("    Tipos de Equipo")}
                onClick={() => navigate('/truck-types')}
                selected={isCurrentPath('/truck-types')}
              />
            )}
            {expandedConfigGroups.rampla && hasPermission('trucks', 'listView') && (
              <SideNavigationSubItem
                icon="inventory"
                text={autoT("    Equipos")}
                onClick={() => navigate('/trucks')}
                selected={isCurrentPath('/trucks')}
              />
            )}

            {/* Grupo Usuarios y Roles */}
            {(hasPermission('users', 'listView') || hasPermission('roles', 'listView')) && (
              <SideNavigationSubItem
                icon="employee"
                text={expandedConfigGroups.usuarios ? "▼ Usuarios y Roles" : "▶ Usuarios y Roles"}
                onClick={() => toggleConfigGroup('usuarios')}
                selected={isCurrentPath('/roles') || isCurrentPath('/user-list')}
              />
            )}
            {expandedConfigGroups.usuarios && hasPermission('roles', 'listView') && (
              <SideNavigationSubItem
                icon="key-user-settings"
                text={autoT("    Roles")}
                onClick={() => navigate('/roles')}
                selected={isCurrentPath('/roles')}
              />
            )}
            {expandedConfigGroups.usuarios && hasPermission('users', 'listView') && (
              <SideNavigationSubItem
                icon="person-placeholder"
                text={autoT("    Usuarios")}
                onClick={() => navigate('/user-list')}
                selected={isCurrentPath('/user-list')}
              />
            )}
          </SideNavigationItem>
        )}

        {/* ADMIN TENANT */}
        {isSaasAdmin() && (
          <SideNavigationItem
            icon="employee"
            text={autoT("Admin Tenant")}
            expanded={location.pathname.includes('/admin')}
            onClick={() => toggleExpand('plataforma')}
            selected={location.pathname.includes('/admin')}
          >
            <SideNavigationSubItem
              icon="customer"
              text={autoT("Tenants")}
              onClick={() => navigate('/admin/tenants')}
              selected={isCurrentPath('/admin/tenants')}
            />
          </SideNavigationItem>
        )}

        <SideNavigationItem
          icon="log"
          text={autoT("Salir")}
          onClick={requestLogout}
        />

      </SideNavigation>
    </div>
  );
}

export default SideMenu;
